import { useState } from "react";

import { PhotoUpload } from "@/components/common";
import { Button } from "@/components/ui";
import { AvatarSelector } from "@/features/Create/components/AvatarSelector";
import type { FieldValue } from "@/features/Create/types";
import { useGetAvatarsQuery } from "@/services/api-hooks/avatars";
import { gtmService } from "@/services/gtm-service";
import type { FieldDef } from "@/types/content";

import { FieldShell } from "./FieldShell";

type Mode = "avatar" | "upload";

interface Props {
  field: FieldDef;
  value: FieldValue;
  onChange: (value: FieldValue) => void;
}

export const PersonImageField = ({ field, value, onChange }: Props) => {
  const {
    data: avatarsData,
    isLoading: isLoadingAvatars,
    isError: isAvatarsError,
    refetch: refetchAvatars,
  } = useGetAvatarsQuery();

  const avatars = !isAvatarsError && !isLoadingAvatars && avatarsData ? avatarsData : [];

  const current = typeof value === "string" ? value : "";
  const selectedAvatarId = avatars.find((a) => a.image_url === current)?.id ?? null;
  const [mode, setMode] = useState<Mode>(
    current && !selectedAvatarId ? "upload" : "avatar",
  );

  return (
    <FieldShell label={field.label} description={field.description}>
      <div className="mb-3 flex flex-wrap gap-2">
        <Button
          onClick={() => setMode("avatar")}
          variant={mode === "avatar" ? "default" : "outline"}
        >
          Choose Avatar
        </Button>
        <Button
          onClick={() => setMode("upload")}
          variant={mode === "upload" ? "default" : "outline"}
        >
          Upload Photo
        </Button>
      </div>

      {mode === "avatar" ? (
        isAvatarsError ? (
          <div className="flex flex-col items-center rounded-xl border-2 border-red-200 border-dashed py-6">
            <p className="mb-2 text-red-500 text-sm">Failed to load avatars</p>
            <Button
              variant="ghost"
              onClick={() => refetchAvatars()}
              className="text-primary hover:bg-background hover:text-primary-hover"
            >
              Retry
            </Button>
          </div>
        ) : (
          <AvatarSelector
            avatars={avatars}
            isLoading={isLoadingAvatars}
            selectedId={selectedAvatarId}
            onSelect={(id) => {
              const avatar = id ? avatars.find((a) => a.id === id) : null;
              onChange(avatar?.image_url);
            }}
          />
        )
      ) : (
        <PhotoUpload
          label="Your Photo"
          values={current ? [current] : []}
          onChange={(arr) => onChange(arr[0])}
          hint="Clear, front-facing photo works best"
          onSelect={() => gtmService.trackCreatePhotoUploadTry()}
          onSuccess={(photos) =>
            gtmService.trackCreatePhotoUploadScs({ photo_count: photos.length })
          }
          onError={(message) => gtmService.trackCreatePhotoUploadFail({ error: message })}
        />
      )}
    </FieldShell>
  );
};
